import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Menu, Download } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import type { CityConfig } from '@/lib/city'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface HeaderProps {
  title: string
  city: CityConfig | null
  lang: 'el' | 'en'
  onToggleLanguage: () => void
  onShare: () => void
  showCopied: boolean
  copiedText: string
  t: {
    switchToEnglish: string
    switchToGreek: string
    install: string
    installApp: string
    share: string
  }
}

// Detect iOS Safari
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
}

// Detect if running in standalone mode (already installed)
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
}

// Detect if we're on a subdomain (city-specific site)
const isSubdomain = () => {
  const hostname = window.location.hostname
  const isSubdomain = hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname.includes('.')
  console.log('Hostname:', hostname, 'Is subdomain:', isSubdomain)
  return isSubdomain
}

export function Header({
  title,
  city,
  lang,
  onToggleLanguage,
  onShare,
  showCopied,
  copiedText,
  t
}: HeaderProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstall, setShowInstall] = useState(false)
  const [showIOSInstructions, setShowIOSInstructions] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    console.log('Header useEffect - checking install conditions')
    console.log('Is standalone:', isStandalone())
    console.log('Is subdomain:', isSubdomain())
    console.log('Is iOS:', isIOS())
    
    // Don't show install if already in standalone mode or not on a subdomain
    if (isStandalone() || !isSubdomain()) {
      console.log('Hiding install button - standalone or not subdomain')
      setShowInstall(false)
      return
    }

    // Listen for app installed event (to hide install button if installed via browser UI)
    const handleAppInstalled = () => {
      console.log('App installed event fired')
      setShowInstall(false)
      setDeferredPrompt(null)
    }

    // For iOS Safari, show install option (will show instructions)
    if (isIOS()) {
      console.log('Showing install button for iOS')
      setShowInstall(true)
    } else {
      console.log('Setting up beforeinstallprompt listener for non-iOS')
      // For browsers that support beforeinstallprompt (Chrome, Edge, etc.)
      const handler = (e: Event) => {
        e.preventDefault()
        console.log('beforeinstallprompt event fired')
        setDeferredPrompt(e as BeforeInstallPromptEvent)
        setShowInstall(true)
      }

      window.addEventListener('beforeinstallprompt', handler)
      window.addEventListener('appinstalled', handleAppInstalled)

      return () => {
        window.removeEventListener('beforeinstallprompt', handler)
        window.removeEventListener('appinstalled', handleAppInstalled)
      }
    }

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstall = async () => {
    // For iOS, show instructions modal
    if (isIOS()) {
      setShowIOSInstructions(true)
      return
    }

    // For browsers with native install prompt
    if (!deferredPrompt) {
      console.log('No deferred prompt available')
      return
    }

    console.log('Triggering install prompt')
    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      console.log(`Install prompt outcome: ${outcome}`)

      if (outcome === 'accepted') {
        setShowInstall(false)
      }
    } catch (error) {
      console.error('Error during install prompt:', error)
    }
    setDeferredPrompt(null)
  }

  const handleMenuAction = (action: () => void) => {
    action()
    setIsMenuOpen(false)
  }

  return (
    <header className="app-header">
      {/* Mobile hamburger menu - replaces bus icon on mobile */}
      <div className="flex items-center gap-2">
        <div className="sm:hidden">
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <button className="hamburger-btn" title="Menu">
                <Menu className="h-6 w-6 text-text-primary" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] border-border">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-2">
                {showInstall && (
                  <button
                    className="menu-item"
                    onClick={() => handleMenuAction(handleInstall)}
                  >
                    <span className="menu-icon">📲</span>
                    <span>{lang === 'el' ? t.install : t.installApp}</span>
                  </button>
                )}
                <button
                  className="menu-item"
                  onClick={() => handleMenuAction(onToggleLanguage)}
                >
                  <span className="menu-icon">{lang === 'el' ? '🇬🇧' : '🇬🇷'}</span>
                  <span>{lang === 'el' ? t.switchToEnglish : t.switchToGreek}</span>
                </button>
                 <Link to="/lines" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                   <span className="menu-icon">🚌</span>
                   <span>{lang === 'el' ? 'Γραμμές' : 'Lines'}</span>
                 </Link>
                 <Link to="/plan" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                   <span className="menu-icon">🧭</span>
                   <span>{lang === 'el' ? 'Σχεδιασμός διαδρομής' : 'Plan a trip'}</span>
                 </Link>
                 <Link to="/near-me" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                   <span className="menu-icon">📍</span>
                   <span>{lang === 'el' ? 'Στάσεις κοντά μου' : 'Stops Near Me'}</span>
                 </Link>
                 <button
                   className="menu-item"
                   onClick={() => handleMenuAction(onShare)}
                 >
                   <span className="menu-icon">📤</span>
                   <span>{lang === 'el' ? t.share : 'Share'}</span>
                 </button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
        <Link to="/">
          <h1>
            <span className="hidden sm:inline">🚌 </span>
            NextBus
            {city && (
              <span className="text-sm font-normal opacity-75 ml-1">
                {lang === 'el' ? city.nameGreek : city.name}
              </span>
            )}
          </h1>
        </Link>
      </div>

      {/* Desktop controls - hidden on mobile */}
      <div className="header-controls hidden sm:flex">
        {showInstall && (
          <button className="install-btn" onClick={handleInstall} title="Install App">
            📲
          </button>
        )}
        <button
          className="lang-btn"
          onClick={onToggleLanguage}
          title={lang === 'el' ? t.switchToEnglish : t.switchToGreek}
        >
          {lang === 'el' ? '🇬🇧' : '🇬🇷'}
        </button>
      </div>

      {showCopied && <div className="copied-toast">{copiedText}</div>}



      {/* iOS Installation Instructions Modal */}
      {showIOSInstructions && (
        <div className="ios-install-modal" onClick={() => setShowIOSInstructions(false)}>
          <div className="ios-install-content" onClick={(e) => e.stopPropagation()}>
            <h3>{lang === 'el' ? 'Εγκατάσταση στο iOS' : 'Install on iOS'}</h3>
            <div className="ios-install-steps">
              <div className="ios-step">
                <span className="ios-step-icon">1</span>
                <span>{lang === 'el' ? 'Πατήστε το κουμπί' : 'Tap the'} <span className="ios-share-icon">⬆️</span> {lang === 'el' ? t.share : 'Share button'}</span>
              </div>
              <div className="ios-step">
                <span className="ios-step-icon">2</span>
                <span>{lang === 'el' ? 'Κάντε scroll και πατήστε' : 'Scroll and tap'} "<strong>{lang === 'el' ? 'Προσθήκη στην Αρχική' : 'Add to Home Screen'}</strong>"</span>
              </div>
              <div className="ios-step">
                <span className="ios-step-icon">3</span>
                <span>{lang === 'el' ? 'Πατήστε' : 'Tap'} "<strong>{lang === 'el' ? 'Προσθήκη' : 'Add'}</strong>"</span>
              </div>
            </div>
            <button className="ios-close-btn" onClick={() => setShowIOSInstructions(false)}>
              {lang === 'el' ? 'Κατάλαβα' : 'Got it'}
            </button>
          </div>
        </div>
      )}
    </header>
  )
}
